import { gql } from '@apollo/client';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from '../../utils/router/v3Bridge';
import CallOut from '../../components/CallOut';
import type { AdminBillingNotice_BillingSummaryFragment } from '../../graphql/generated/graphqlTypes';
import useSeatLimits from '../../invitation/useSeatLimits';
import formatCurrency from '../../utils/formatCurrency';
import pricingPath, { openPricingModal } from '../../utils/url/pricingPath';

interface AdminBillingNoticeProps {
  billingSummary: AdminBillingNotice_BillingSummaryFragment;
  isTrialEnded: boolean;
}

const BILLING_SUMMARY_FRAGMENT = gql`
  fragment AdminBillingNotice_billingSummary on BillingSummary {
    status
    startAt
    trialEndAt
    periodEndAt
    plan {
      tier
    }
    planBill {
      amount
      billAt
    }
    overageBill {
      amount
      billAt
    }
    source {
      id
    }
    pendingActionUrl
    chargeAutomatically
  }
`;

const AdminBillingNotice = ({
  billingSummary: {
    status,
    startAt,
    trialEndAt,
    periodEndAt,
    plan,
    planBill,
    overageBill,
    source,
    pendingActionUrl,
    chargeAutomatically,
  },
  isTrialEnded,
}: AdminBillingNoticeProps) => {
  const dispatch = useDispatch();

  const { isSeatLimitMaxed } = useSeatLimits();

  const learnMore = (
    <>
      {' '}
      <Link
        to={pricingPath()}
        onClick={(e) => {
          e.preventDefault();
          openPricingModal(dispatch);
        }}
      >
        Learn about our plans here
      </Link>
      .
    </>
  );

  if (plan && plan.tier === 'FREE') {
    if (isSeatLimitMaxed)
      return (
        <CallOut variant="danger">
          Your team has reached the seat limit for our free plan. Please upgrade
          to a paid plan to add additional teammates.
        </CallOut>
      );

    // Trial was started at one point, and user is back on free plan
    if (startAt)
      return (
        <CallOut variant="danger">
          <b>Your trial has ended and you’re now on the Free plan.</b> Subscribe
          to Startup or Business to unlock private topics, unlimited users, and
          more. {learnMore}
        </CallOut>
      );

    return (
      <CallOut>
        <b>Your team is on the free plan.</b> Start a 14 day trial for Startup
        or Business plan to unlock private topics, unlimited users, and more.
        {learnMore}
      </CallOut>
    );
  }

  if (pendingActionUrl)
    return (
      <CallOut variant="danger">
        Your payment is still unconfirmed. Please{' '}
        <a href={pendingActionUrl}>click here to confirm your payment</a> to us
        with your bank.
      </CallOut>
    );

  switch (status) {
    case 'TRIALING':
      return (
        <CallOut variant="warning">
          Your team is on a trial, ending on {moment(trialEndAt).format('LL')}.
          Subscribe to continue benefiting from this plan after your trial
          expires. {learnMore}
        </CallOut>
      );

    case 'CANCELED':
      return (
        <CallOut variant="danger">
          Your subscription to Slab has been canceled. Use the form below to
          reactivate your subscription at any time.
        </CallOut>
      );

    case 'UNPAID':
      return (
        <CallOut variant="danger">
          Your account is temporarily locked due to problems with your billing.
          Please contact <a href="mailto:support@slab.com">Slab Support</a> so
          we can remedy the issue and reactivate your account.
        </CallOut>
      );

    case 'PAST_DUE': {
      if (isTrialEnded)
        return (
          <CallOut variant="danger">
            <b>Your trial has ended.</b> Subscribe to a plan to continue using
            Slab. {learnMore}
          </CallOut>
        );

      if (!chargeAutomatically)
        return (
          <CallOut variant="danger">
            <b>Payment past due.</b> To continue using Slab, please settle the
            invoice sent to your billing email address or contact{' '}
            <a href="mailto:support@slab.com">Slab Support</a>.
          </CallOut>
        );

      if (source)
        return (
          <CallOut variant="danger">
            <b>Payment unsuccessful.</b> The credit card on file was declined.
            Try a different card.
          </CallOut>
        );

      return (
        <CallOut variant="danger">
          <b>Payment unsuccessful.</b> We couldn&apos;t find a valid payment
          source on file. Add your payment information to continue using Slab.
        </CallOut>
      );
    }

    case 'ACTIVE':
      if (overageBill.amount > 0)
        return (
          <CallOut>
            An adjustment will be applied to your account for{' '}
            {formatCurrency(overageBill.amount)} on{' '}
            {moment(overageBill.billAt).format('LL')}, for new users added
            recently
          </CallOut>
        );

      if (overageBill.amount < 0)
        return (
          <CallOut>
            Your account will be credited {formatCurrency(-overageBill.amount)}{' '}
            on {moment(overageBill.billAt).format('LL')}, for users deactivated
            or downgraded recently
          </CallOut>
        );

      return (
        <CallOut>
          Your next bill will be on {moment(planBill.billAt).format('LL')}, for{' '}
          {formatCurrency(planBill.amount)}
        </CallOut>
      );

    case 'CANCELED_AND_ACTIVE':
      return (
        <CallOut variant="warning">
          <b>You have canceled your subscription.</b> You can continue to use
          Slab&apos;s {plan?.tier.toLowerCase()} features until{' '}
          {moment(periodEndAt).format('LL')}
          {isSeatLimitMaxed
            ? ''
            : ', after which you will be automatically downgraded to the free plan'}
          .
        </CallOut>
      );

    default:
      return null;
  }
};

AdminBillingNotice.fragments = { billingSummary: BILLING_SUMMARY_FRAGMENT };

export default AdminBillingNotice;
